/**
 * Heritage Amusements theme — main JavaScript
 *
 * @package WPSCT
 */

import { HeroAnimations } from "./animations/HeroAnimations";
import { ScrollAnimations } from "./animations/ScrollAnimations";
import { FullscreenMenu } from "./components/FullscreenMenu";
import { LocationArchiveFilters } from "./components/LocationArchiveFilters";
import { SupportFaqGroups } from "./components/FaqGroup";
import { TestimonialsSlider } from "./components/TestimonialsSlider";
import { ContactForm } from "./components/ContactForm";
import { LeasingInquiryForm } from "./components/LeasingInquiryForm";

/**
 * Initialize theme
 */
class WPSHeritageAmusementsTheme {
  constructor() {
    this.init();
  }

  /**
   * Initialize all components
   */
  init() {
    // Wait for DOM to be ready
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => this.onReady());
    } else {
      this.onReady();
    }
  }

  /**
   * On DOM ready
   */
  onReady() {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.initLoader();
      });
    });

    // this.initSplitText();
    this.initAOS();
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.initAnimations();
      });
    });
    this.initFullscreenMenu();
    this.initContactForm();
    this.initLeasingInquiryForm();
    this.initTestimonialsSlider();
    this.initLocationArchiveFilters();
    this.initSupportFaqGroups();
  }

  /**
   * Initialise SplitText
   */
  // initSplitText() {
  //   if (typeof gsap === "undefined" || typeof SplitText === "undefined") {
  //     console.warn("GSAP or SplitText not loaded");
  //     return;
  //   }

  //   gsap.registerPlugin(SplitText);

  //   const mainTitles = [...document.querySelectorAll("[data-main-title]")];
  //   if (mainTitles) {
  //     mainTitles.forEach((title) => {
  //       const split = new SplitText(title, {
  //         type: "chars",
  //         charsClass: "text-chars",
  //       });
  //     });
  //   }

  //   const subTitles = [...document.querySelectorAll("[data-sub-title]")];
  //   if (subTitles) {
  //     subTitles.forEach((title) => {
  //       const split = new SplitText(title, {
  //         type: "words,chars",
  //         wordsClass: "text-words",
  //         charsClass: "text-chars",
  //       });
  //     });
  //   }
  // }

  /**
   * Initialize AOS (Animate On Scroll)
   */
  initAOS() {
    if (typeof AOS !== "undefined") {
      AOS.init({
        duration: 800,
        easing: "ease-in-out-sine",
        once: true,
        offset: 100,
        delay: 0,
      });
    }
  }

  /**
   * Initialize Loader
   */
  initLoader() {
    if (typeof gsap === "undefined") {
      return;
    }

    const section = document.querySelector(".wps-header-preloader__wrapper");
    if (!section) return;

    const heroSection = document.querySelector(".wps-header-hero__wrapper");
    const html = document.documentElement;
    const body = document.body;

    const previousHtmlOverflow = html.style.overflow;
    const previousBodyOverflow = body.style.overflow;
    const previousBodyHeight = body.style.height;

    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    body.style.height = "100vh";

    if (heroSection) {
      const chars = [...heroSection.querySelectorAll(".text-chars")];

      chars.forEach((char) => {
        char.textContent = Math.floor(Math.random() * 10);
      });
    }

    const tc = document.querySelector(".timecode");

    let startTime = performance.now();

    function pad(n) {
      return n.toString().padStart(2, "0");
    }

    function updateTimer() {
      const now = performance.now();
      const elapsed = now - startTime;

      const totalFrames = Math.floor(elapsed / (1000 / 24));

      let f = totalFrames % 24;
      let s = Math.floor(totalFrames / 24) % 60;
      let m = Math.floor(totalFrames / 1440) % 60;

      if (tc) {
        tc.textContent = `00:${pad(m)}:${pad(s)}:${pad(f)}`;
      }

      if (elapsed < 2000) {
        requestAnimationFrame(updateTimer);
      }
    }

    requestAnimationFrame(updateTimer);

    function finish() {
      const restoreScroll = () => {
        html.style.overflow = previousHtmlOverflow;
        body.style.overflow = previousBodyOverflow;
        body.style.height = previousBodyHeight;
      };

      const onComplete = () => {
        restoreScroll();

        if (section) {
          section.remove();
        }

        if (typeof HeroAnimations === "function") {
          new HeroAnimations();
        }

        if (typeof ScrollTrigger !== "undefined") {
          ScrollTrigger.refresh(true);
        }
      };

      if (!section) {
        onComplete();
        return;
      }

      gsap.to(section, {
        autoAlpha: 0,
        duration: 0.2,
        onComplete,
      });
    }

    gsap.delayedCall(2, finish);
  }

  /**
   * Initialize GSAP animations
   */
  initAnimations() {
    if (typeof gsap !== "undefined") {
      new ScrollAnimations();
    }
  }

  /**
   * Initialize Fullscreen Menu
   */
  initFullscreenMenu() {
    new FullscreenMenu();
  }

  /**
   * Initialize Faq Groups
   */
  initSupportFaqGroups() {
    new SupportFaqGroups();
  }

  /**
   * Initialize Contact Form
   */
  initContactForm() {
    new ContactForm();
  }

  /**
   * Initialize leasing inquiry form.
   */
  initLeasingInquiryForm() {
    new LeasingInquiryForm();
  }

  /**
   * Initialize testimonials carousel (Swiper).
   */
  initTestimonialsSlider() {
    new TestimonialsSlider();
  }

  /**
   * Initialize locations archive partner + region filters.
   */
  initLocationArchiveFilters() {
    new LocationArchiveFilters();
  }
}

// Initialize theme
new WPSHeritageAmusementsTheme();
